// src/pages/certificate-search/index.jsx
//
// Buscador de certificados e instrumentos para el jefe y los administrativos.
// Una fila por instrumento con su certificado vigente. El texto libre busca por
// nº de serie, TAG, ID, nº de certificado, marca, modelo y cliente; además hay
// filtros por tipo, vigencia y rango de fechas de calibración.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CertLabelModal from '../../components/CertLabelModal';
import { searchCertificates } from '../../utils/api-certificates';
import { INSTRUMENT_TYPE_OPTIONS, instrumentTypeLabel } from '../../utils/instrument-types';
import { useAuth } from '../../components/system/AppGate';

const VIGENCIA_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'vigente', label: 'Vigente' },
  { value: 'por_vencer', label: 'Por vencer (30 días)' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'sin_certificado', label: 'Sin certificado' },
];

const VIGENCIA_STYLE = {
  vigente: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  por_vencer: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  vencido: 'border-rose-500/40 bg-rose-500/10 text-rose-300',
  sin_certificado: 'border-slate-500/30 bg-slate-500/10 text-slate-400',
  sin_vencimiento: 'border-slate-500/30 bg-slate-500/10 text-slate-400',
};
const VIGENCIA_LABEL = {
  vigente: 'Vigente',
  por_vencer: 'Por vencer',
  vencido: 'Vencido',
  sin_certificado: 'Sin certificado',
  sin_vencimiento: 'Sin vencimiento',
};

function fmtDate(v) {
  if (!v) return '—';
  const [y, m, d] = String(v).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : '—';
}

const EMPTY = { q: '', type: '', vigencia: '', from: '', to: '' };

export default function CertificateSearchPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const [filters, setFilters] = useState(EMPTY);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [labelFor, setLabelFor] = useState(null);
  const timer = useRef(null);

  const run = useCallback(async (f) => {
    setLoading(true);
    setErr(null);
    try {
      setRows(await searchCertificates(f));
    } catch (e) {
      console.error('[BuscadorCertificados]', e);
      setErr(e?.message || 'No se pudo buscar.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce: el texto se tipea, los selects/fechas disparan enseguida.
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(filters), filters.q ? 350 : 0);
    return () => clearTimeout(timer.current);
  }, [filters, run]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const hayFiltros = useMemo(() => Object.values(filters).some(Boolean), [filters]);

  return (
    <div className="p-6 space-y-5 max-w-[1500px] mx-auto">
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold text-white">Buscar certificados e instrumentos</h1>
        <p className="text-sm text-slate-400">
          Buscá por <b className="text-slate-200">nº de serie, TAG, ID, nº de certificado, marca, modelo o cliente</b>, y
          filtrá por tipo, vigencia y fecha de calibración.
        </p>

        {['admin', 'lab_manager'].includes(role) && (
          <section className="rounded-xl border border-sky-500/30 bg-sky-500/[0.07] p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-2">
                <div>
                  <h2 className="font-semibold text-sky-100">Archivo de certificados para resguardo</h2>
                  <p className="mt-1 max-w-4xl text-sm leading-6 text-slate-300">
                    Usá esta sección para preparar certificados en un archivo ZIP y guardarlos en el Drive institucional.
                    El sistema conserva los originales durante <b className="text-white">2 años y 60 días</b>; Drive debe
                    conservar la copia maestra durante <b className="text-white">10 años</b>.
                  </p>
                </div>

                <ol className="grid gap-2 text-xs text-slate-300 sm:grid-cols-3">
                  <li className="rounded-lg border border-white/10 bg-slate-950/30 px-3 py-2">
                    <b className="text-sky-200">1. Descargar</b><br />Prepará y descargá el ZIP desde el sistema.
                  </li>
                  <li className="rounded-lg border border-white/10 bg-slate-950/30 px-3 py-2">
                    <b className="text-sky-200">2. Guardar en Drive</b><br />Subí el mismo ZIP sin modificarlo.
                  </li>
                  <li className="rounded-lg border border-white/10 bg-slate-950/30 px-3 py-2">
                    <b className="text-sky-200">3. Comprobar</b><br />Descargalo desde Drive y verificá la copia.
                  </li>
                </ol>

                <p className="text-xs font-medium text-amber-200">
                  Importante: descargar el ZIP no confirma el resguardo. La tarea termina únicamente después de comprobar
                  en el sistema el archivo recuperado desde Drive.
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate('/ops/certificate-archive')}
                className="shrink-0 rounded-lg border border-sky-400/50 bg-sky-500/15 px-5 py-3 text-sm font-semibold text-sky-100 transition hover:bg-sky-500/25"
              >
                Abrir archivo de certificados
              </button>
            </div>
          </section>
        )}
      </div>

      {/* Filtros */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
        <div className="relative">
          <span className="absolute inset-y-0 left-3.5 flex items-center text-slate-400 pointer-events-none">🔍</span>
          <input
            autoFocus
            value={filters.q}
            onChange={(e) => set('q', e.target.value)}
            placeholder="Nº de serie, TAG, ID, nº de certificado, marca, modelo o cliente…"
            className="w-full rounded-xl bg-slate-800/60 border border-white/10 pl-11 pr-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <label className="text-xs text-slate-400">
            Tipo de instrumento
            <select
              value={filters.type}
              onChange={(e) => set('type', e.target.value)}
              className="mt-1 w-full rounded-lg bg-slate-800/60 border border-white/10 px-2 py-2 text-sm text-white"
            >
              <option value="">Todos</option>
              {INSTRUMENT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <label className="text-xs text-slate-400">
            Vigencia
            <select
              value={filters.vigencia}
              onChange={(e) => set('vigencia', e.target.value)}
              className="mt-1 w-full rounded-lg bg-slate-800/60 border border-white/10 px-2 py-2 text-sm text-white"
            >
              {VIGENCIA_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <label className="text-xs text-slate-400">
            Calibrado desde
            <input type="date" value={filters.from} onChange={(e) => set('from', e.target.value)}
              className="mt-1 w-full rounded-lg bg-slate-800/60 border border-white/10 px-2 py-2 text-sm text-white" />
          </label>

          <label className="text-xs text-slate-400">
            Calibrado hasta
            <input type="date" value={filters.to} onChange={(e) => set('to', e.target.value)}
              className="mt-1 w-full rounded-lg bg-slate-800/60 border border-white/10 px-2 py-2 text-sm text-white" />
          </label>

          <div className="flex items-end">
            <button
              onClick={() => setFilters(EMPTY)}
              disabled={!hayFiltros}
              className="w-full px-3 py-2 rounded-lg border border-white/15 text-sm text-slate-300 hover:bg-white/5 disabled:opacity-40"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{err}</div>
      )}

      {/* Resultados */}
      <div className="text-xs text-slate-500">
        {loading ? 'Buscando…' : rows === null ? '' : `${rows.length} resultado${rows.length === 1 ? '' : 's'}${rows.length >= 200 ? ' (mostrando los primeros 200 — afiná los filtros)' : ''}`}
      </div>

      {rows !== null && rows.length === 0 && !loading ? (
        <div className="rounded-xl border border-white/10 bg-white/5 px-5 py-8 text-center text-slate-400">
          No se encontraron instrumentos con esos filtros.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-400 text-xs">
              <tr>
                <th className="text-left px-3 py-2 font-medium">ID</th>
                <th className="text-left px-3 py-2 font-medium">Instrumento</th>
                <th className="text-left px-3 py-2 font-medium">N° de serie</th>
                <th className="text-left px-3 py-2 font-medium">TAG</th>
                <th className="text-left px-3 py-2 font-medium">Tipo</th>
                <th className="text-left px-3 py-2 font-medium">Cliente</th>
                <th className="text-left px-3 py-2 font-medium">Certificado</th>
                <th className="text-left px-3 py-2 font-medium">Vence</th>
                <th className="text-left px-3 py-2 font-medium">Estado</th>
                <th className="text-right px-3 py-2 font-medium sticky right-0 bg-[#161b22] border-l border-white/10">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((r) => (
                <tr key={r.instrument_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-3 py-2 font-semibold text-white whitespace-nowrap">#{r.lab_id ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-200">{[r.brand, r.model].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-3 py-2 text-slate-300">{r.serial || '—'}</td>
                  <td className="px-3 py-2 text-slate-300">{r.tag || '—'}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{instrumentTypeLabel(r.type)}</td>
                  <td className="px-3 py-2 text-slate-300">{r.client_name || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.certificate_number ? (
                      <span className="text-white font-medium">N° {r.certificate_number}</span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                    {r.calibration_date && (
                      <span className="block text-[11px] text-slate-500">{fmtDate(r.calibration_date)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtDate(r.valid_until)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${VIGENCIA_STYLE[r.vigencia] || ''}`}>
                      {VIGENCIA_LABEL[r.vigencia] || r.vigencia}
                    </span>
                  </td>
                  <td className="px-3 py-2 sticky right-0 bg-[#101620] border-l border-white/10">
                    <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/instrument-details/${r.instrument_id}`)}
                        title="Abrir la ficha del instrumento"
                        className="px-2 py-1 rounded border border-white/15 text-xs text-slate-300 hover:bg-white/5"
                      >
                        Ficha
                      </button>
                      {r.pdf_url && (
                        <a
                          href={r.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Descargar el certificado"
                          className="px-2 py-1 rounded border border-white/15 text-xs text-slate-300 hover:bg-white/5"
                        >
                          PDF
                        </a>
                      )}
                      {r.certificate_number && r.public_token && (
                        <button
                          onClick={() => setLabelFor({
                            certificate_number: r.certificate_number,
                            public_token: r.public_token,
                          })}
                          title="Imprimir la etiqueta QR"
                          className="px-2 py-1 rounded border border-white/15 text-xs text-slate-300 hover:bg-white/5"
                        >
                          🏷
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CertLabelModal
        open={!!labelFor}
        certificate={labelFor}
        onClose={() => setLabelFor(null)}
      />
    </div>
  );
}
